import styled from "styled-components";
import { useEffect, useState } from "react";
import { X, CalendarRange, ArrowRight, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { apiFetch } from "./api";
import DatePickField from "./DatePickField";
import { formatDate } from "./dateFormat";

// Move a reserved walk-in to another date.
//
// The whole window shifts by the same delta — the number of nights is preserved — so the FO picks one
// date and the check-out follows. The preview below it is not decoration: the move pushes room
// inventory to eZee per night, and the FO is the one who has to recognise a night that will be given
// back to the channel or taken from it.
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  /* Above the drawer panel (1400). */
  z-index: 1500;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  max-width: 520px;
  width: 100%;
`;

const Head = styled.div`
  align-items: flex-start;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.6rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.35rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.8rem 1.1rem;
`;

const Current = styled.div`
  background: #faf9f6;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  color: #4a4f63;
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.65rem;
`;

const Window = styled.div`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  font-size: 0.9rem;
  font-weight: 800;
  gap: 0.35rem;
  margin-top: 0.15rem;
`;

const Rows = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 9px;
  margin-top: 0.7rem;
  overflow: hidden;
`;

const Row = styled.div`
  align-items: center;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.5rem;
  font-size: 0.8rem;
  padding: 0.35rem 0.6rem;
  &:last-child {
    border-bottom: 0;
  }
`;

const Kind = styled.span`
  background: ${(props) => (props.$add ? "#fdecea" : "#e6f6ee")};
  border-radius: 999px;
  color: ${(props) => (props.$add ? "#b42318" : "#1b6e56")};
  flex: 0 0 auto;
  font-size: 0.66rem;
  font-weight: 800;
  padding: 0.05rem 0.4rem;
`;

const RowDate = styled.span`
  color: ${theme.ink};
  font-weight: 700;
`;

const RowNote = styled.span`
  color: #4a4f63;
  margin-left: auto;
  text-align: right;
`;

const Warn = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border-radius: 8px;
  color: #8a5a00;
  display: flex;
  font-size: 0.78rem;
  font-weight: 700;
  gap: 0.3rem;
  margin-top: 0.6rem;
  padding: 0.45rem 0.6rem;
`;

// Money already collected against money the new nights are worth.
const Delta = styled.div`
  background: ${(props) => (props.$short ? "#fdecea" : "#e6f6ee")};
  border-radius: 9px;
  color: ${(props) => (props.$short ? "#b42318" : "#1b6e56")};
  margin-top: 0.6rem;
  padding: 0.5rem 0.65rem;
`;

const Money = styled.div`
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  justify-content: space-between;
`;

const DeltaNote = styled.div`
  align-items: center;
  display: flex;
  font-size: 0.8rem;
  font-weight: 800;
  gap: 0.25rem;
  margin-top: 0.3rem;
`;

const Err = styled.div`
  background: #fdecea;
  border-radius: 8px;
  color: #b42318;
  font-size: 0.8rem;
  font-weight: 700;
  margin-top: 0.6rem;
  padding: 0.45rem 0.6rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  justify-content: flex-end;
  padding: 0.7rem 1.1rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.3rem;
  padding: 0.5rem 0.9rem;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Primary = styled(Btn)`
  background: ${theme.ink};
  border: 0;
  color: #ffffff;
`;

const RescheduleModal = (props) => {
  const { t, lang } = useT();
  const line = props.line || {};
  const [date, setDate] = useState(() =>
    (line.check_in_date || "").slice(0, 10),
  );
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const changed = date && date !== (line.check_in_date || "").slice(0, 10);
  const fmt = (value) => `Rp${Number(value || 0).toLocaleString("en-US")}`;

  // Preview on every change: the plan depends on what else holds the room on the target window, so it
  // cannot be derived on the client.
  // The preview is stored WITH the date it was computed for, so "no preview yet" is a comparison
  // rather than a second piece of state cleared from inside the effect.
  const plan = preview?.for === date ? preview.data : null;

  useEffect(() => {
    if (!changed) {
      return undefined;
    }
    let active = true;
    apiFetch(
      `/frontdesk_v2/bookings/${props.bookingId}/reschedule_preview?reschedule_line_id=${line.id}&new_check_in_date=${date}&line_id=${line.id}`,
    )
      .then((res) => {
        if (!active) return;
        if (res?.data) {
          setPreview({ for: date, data: res.data });
          setError("");
        } else {
          setPreview(null);
          setError(res?.message || t("error_body"));
        }
      })
      .catch(() => {
        if (active) {
          setPreview(null);
          setError(t("error_body"));
        }
      });
    return () => {
      active = false;
    };
  }, [date, changed, props.bookingId, line.id, t]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch(
        `/frontdesk_v2/bookings/${props.bookingId}/reschedule`,
        {
          method: "POST",
          body: JSON.stringify({
            reschedule_line_id: line.id,
            new_check_in_date: date,
            line_id: line.id,
          }),
        },
      );
      if (res?.data?.booking) {
        props.onChanged(res.data, res.message);
      } else {
        setError(res?.message || t("error_body"));
      }
    } catch {
      setError(t("error_body"));
    }
    setBusy(false);
  };

  return (
    <Backdrop onClick={props.onClose}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <CalendarRange size={16} />
            {t("resched_title")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onClose}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          <Current>
            {t("resched_current")}
            <Window>
              {formatDate(line.check_in_date, lang)}
              <ArrowRight size={13} />
              {formatDate(line.check_out_date, lang)}
            </Window>
          </Current>

          <DatePickField
            label={t("resched_new_check_in")}
            value={date}
            onChange={setDate}
          />

          {plan ? (
            <>
              <Current>
                {t("resched_becomes")}
                <Window>
                  {formatDate(plan.new_check_in, lang)}
                  <ArrowRight size={13} />
                  {formatDate(plan.new_check_out, lang)}
                </Window>
              </Current>
              <Rows>
                {(plan.rows || []).map((row) => (
                  <Row key={`${row.change}:${row.date}`}>
                    <Kind $add={row.change === "ADD"}>
                      {row.change === "ADD"
                        ? t("resched_taken")
                        : t("resched_freed")}
                    </Kind>
                    <RowDate>{row.date}</RowDate>
                    <RowNote>{row.ezee}</RowNote>
                  </Row>
                ))}
              </Rows>
              {(plan.warnings || []).map((warning) => (
                <Warn key={warning}>
                  <AlertTriangle size={13} />
                  {warning}
                </Warn>
              ))}
              {/* The rate is resolved per calendar date, so a stay that crosses a weekend or an
                  override is worth a different amount after the move. When money has already been
                  collected, the gap is stated in rupiah rather than left for the FO to work out. */}
              {plan.payment_delta ? (
                <Delta $short={plan.payment_delta.difference_rupiah > 0}>
                  <Money>
                    <span>{t("resched_paid")}</span>
                    <strong>{fmt(plan.payment_delta.paid_rupiah)}</strong>
                  </Money>
                  <Money>
                    <span>{t("resched_new_total")}</span>
                    <strong>{fmt(plan.payment_delta.new_total_rupiah)}</strong>
                  </Money>
                  {plan.payment_delta.difference_rupiah === 0 ? (
                    <DeltaNote>{t("resched_delta_none")}</DeltaNote>
                  ) : (
                    <DeltaNote>
                      <AlertTriangle size={13} />
                      {plan.payment_delta.difference_rupiah > 0
                        ? `${t("resched_delta_short")} ${fmt(plan.payment_delta.difference_rupiah)} — ${t("resched_delta_collect")}`
                        : `${t("resched_delta_over")} ${fmt(-plan.payment_delta.difference_rupiah)}`}
                    </DeltaNote>
                  )}
                </Delta>
              ) : (
                <Warn>
                  <AlertTriangle size={13} />
                  {t("resched_price_note")}
                </Warn>
              )}
            </>
          ) : null}

          {error ? <Err>{error}</Err> : null}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onClose}>
            {t("confirm_cancel")}
          </Ghost>
          <Primary
            type="button"
            disabled={!changed || !plan || busy}
            onClick={submit}
          >
            {t("resched_confirm")}
          </Primary>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default RescheduleModal;
